// @ts-strict-ignore
import React, { useCallback, useContext, useMemo } from 'react';
import { ActionType } from 'flow/store/engine/constants';
import { CaretPositionSuffixTypes, RuleEditorDropdownMode } from 'flow/editor/components/NodeEditorDrawer/types';
import { getRulePathIndexMap } from 'flow/editor/components/NodeEditorDrawer/ThresholdsTab/helpers';
import { RuleEditorContext } from 'flow/editor/components/NodeEditorDrawer/context';
import { StyledSuperset } from 'flow/editor/components/mla/styles';

import EditableThresholdValue from '../EditableThresholdValue';
import RuleThresholdDropdown from '../RuleThresholdDropdown';
import { ruleLineWrapperSideMap, validRightSideAttributeCaretTypes } from './constants';
import { findCaretPosition } from './helpers';
import ThresholdText from '../ThresholdText';
import { ListInclusionItem } from './ListInclusionItem';
import { ThresholdContext } from '../context';

interface RuleLineThresholdItemWrapperProps {
  editable?: boolean;
  rulePath: string;
  lineItems: string[];
}

function RuleLineThresholdItemWrapper({
  editable = true,
  rulePath,
  lineItems,
}: RuleLineThresholdItemWrapperProps) {
  const { mlaUsedInRules } = useContext(RuleEditorContext);

  const {
    threshold,
    thresholdType,
    leftDataType,
  } = useContext(ThresholdContext);

  const canConfigureOperator = (
    threshold.left.action
    && threshold.left.action.type !== ActionType.TAG
    && threshold.left.action.type !== ActionType.OUTCOME_REASON
    && leftDataType !== 'boolean'
    && threshold.operator !== null
  );

  const rulePathIndexMap = useMemo(() => getRulePathIndexMap(
    threshold, canConfigureOperator,
  ), [threshold, canConfigureOperator]);

  const getMLAFootnoteNumber = useCallback((ruleType: 'thresholdLeft' | 'thresholdRight') => (
    mlaUsedInRules.findIndex(rule => (
      rule.publicId === threshold.publicId && rule.type === ruleType))
  ), [mlaUsedInRules, threshold]);

  const mlaFootnoteNumbers = useMemo(() => (
    {
      left: getMLAFootnoteNumber('thresholdLeft'),
      right: threshold.right.attribute && getMLAFootnoteNumber('thresholdRight'),
    }
  ), [getMLAFootnoteNumber, threshold]);

  const getRuleThresholdDropdown = useCallback((mode: RuleEditorDropdownMode) => (
    <RuleThresholdDropdown
      parentRulePath={`${rulePath}[${rulePathIndexMap[mode]}]`}
      mode={mode}
    />
  ), [rulePath, rulePathIndexMap]);

  const buildSide = useCallback((side: 'left' | 'right') => {
    const { caret, dropdown } = ruleLineWrapperSideMap[side];
    const { attribute } = threshold[side];

    const lineItemContainsActionCaret = findCaretPosition(lineItems, caret.action);
    const lineItemContainsAttrCaret = findCaretPosition(lineItems, caret.attribute);

    return (
      <>
        {lineItemContainsActionCaret && getRuleThresholdDropdown(dropdown.action)}
        {attribute && lineItemContainsAttrCaret && (
          <>
            {getRuleThresholdDropdown(dropdown.attribute)}
            {mlaFootnoteNumbers[side] !== undefined && mlaFootnoteNumbers[side] > -1
              ? <StyledSuperset>{mlaFootnoteNumbers[side] + 1}</StyledSuperset>
              : null}
          </>
        )}
      </>
    );
  }, [getRuleThresholdDropdown, lineItems, mlaFootnoteNumbers, threshold]);

  const leftSide = useMemo(() => buildSide('left'), [buildSide]);

  const operator = useMemo(() => {
    const lineItemContainsOperatorCaret = findCaretPosition(
      lineItems, CaretPositionSuffixTypes.THRESHOLD_OPERATOR,
    );

    return (canConfigureOperator && lineItemContainsOperatorCaret)
      ? getRuleThresholdDropdown(RuleEditorDropdownMode.THRESHOLD_OPERATOR)
      : null;
  }, [canConfigureOperator, getRuleThresholdDropdown, lineItems]);

  const lineItemContainsRightAttrValueCaret = useMemo(() => (
    validRightSideAttributeCaretTypes.some(char => lineItems.find(item => item.endsWith(char)))
  ), [lineItems]);

  const rightSide = useMemo(() => {
    if (['date', 'timestamp'].includes(leftDataType) && thresholdType === 'static') {
      return (
        <RuleThresholdDropdown
          parentRulePath={`${rulePath}[${Object.values(rulePathIndexMap).at(-1) + 1}]`}
          mode={RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_DATEPICKER}
        />
      );
    }
    if (['includedin', 'notincludedin'].includes(threshold.operator)) {
      return (
        <ListInclusionItem
          rulePath={`${rulePath}[${Object.values(rulePathIndexMap).at(-1) + 1}]`}
        />
      );
    }
    if (threshold.right.action) return buildSide('right');
    if (lineItemContainsRightAttrValueCaret) {
      return (
        <EditableThresholdValue
          rulePath={rulePath}
          rulePathIndex={Object.values(rulePathIndexMap).at(-1) + 1}
        />
      );
    }
    return null;
  }, [leftDataType, threshold.operator, threshold.right.action, buildSide, thresholdType,
    lineItemContainsRightAttrValueCaret, rulePath, rulePathIndexMap]);

  if (!editable) {
    return (
      <ThresholdText
        leftAttrMLAFootnoteNumber={mlaFootnoteNumbers.left}
        rightAttrMLAFootnoteNumber={mlaFootnoteNumbers.right}
        threshold={threshold}
      />
    );
  }

  return (
    <>
      {leftSide}
      {operator}
      {rightSide}
    </>
  );
}

export default RuleLineThresholdItemWrapper;
